import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDrivers } from '../shared/context/drivers/DriversContext'
import Navigation from './shared/navigation'
import { TooltipBoundaryContext } from './context/TooltipBoundaryContext'
import clx from 'classnames'
import SideDriversTable from '@/modules/drivers/components/SideDriversTable'
import { CollapsibleSidePanel } from '@/modules/shared/components'
import { useParams } from 'react-router-dom'
import { useIsDriverOnly } from '@/modules/shared/permissions'

type Props = {
  children: React.ReactNode
  companyId: string
  viewControlBar?: () => React.JSX.Element
  navigation?: boolean
  heightScreen?: boolean
  fullScreen?: boolean
  hideSidePanel?: boolean
}

export const DriversLayout = ({
  children,
  companyId,
  navigation = false,
  viewControlBar: ViewControlBar,
  heightScreen = false,
  fullScreen = false,
  hideSidePanel = false,
}: Props) => {
  const { t } = useLocalize()
  const { selectedDriverId } = useDrivers()
  const { driverId } = useParams<{ driverId?: string }>()
  const isSingleDriverView = !!driverId
  const isDriverOnly = useIsDriverOnly(companyId)

  const baseClasses = clx(
    'module-driver tw-flex tw-flex-[1]  tw-text-content-base tw-bg-base-100 tw-min-w-0 tw-max-w-full tw-overflow-hidden',
    {
      'tw-h-screen': fullScreen,
      'tw-padding-4 tw-mt-1 tw-mb-1 tw-rounded-xl tw-border tw-border-base-300':
        !fullScreen,
      'tw-h-[calc(100vh-8px)]': !fullScreen,
    },
  )
  const boundaryRef = React.useRef<HTMLDivElement>(null)

  return (
    <TooltipBoundaryContext.Provider value={boundaryRef}>
      <div className={baseClasses} ref={boundaryRef}>
        {!hideSidePanel && !isDriverOnly && (
          <CollapsibleSidePanel
            className="tw-flex tw-w-fit tw-flex-shrink-0 tw-flex-col tw-overflow-hidden tw-border-r tw-border-base-300"
            drawerClassName="tw-w-[300px] tw-max-w-[85vw]"
          >
            <SideDriversTable companyId={companyId} />
          </CollapsibleSidePanel>
        )}
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-x-auto tw-overflow-y-hidden">
          <div className="tw-flex tw-min-h-0 tw-min-w-[900px] tw-flex-1 tw-flex-col">
            {ViewControlBar && <ViewControlBar />}
            <div className="tw-flex tw-min-h-0 tw-flex-1">
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                {navigation && (
                  <Navigation
                    selectedDriverId={selectedDriverId}
                    companyId={companyId}
                  />
                )}
                <main
                  className={`tw-flex tw-h-full tw-flex-1 ${heightScreen ? 'tw-max-h-screen tw-flex-col tw-overflow-hidden' : ''} `}
                >
                  {children}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipBoundaryContext.Provider>
  )
}
